import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import * as React from 'react';
import { useEffect } from 'react';
import { usePermissions } from '@hetivo/hetivo-lib-front-utils';
import { canAccessLocationSettings, canAccessTerritorySettings } from '&/features/settings/utils/settings-scope-access';
import { useMasterContext } from '&/master';

export const Route = createFileRoute('/_authenticated/settings/')({
  beforeLoad: ({ context }): void => {
    if (!context.permissions.isReady) return;

    const { hasPermission } = context.permissions;

    // Tenant admins land on the Organization tab.
    if (hasPermission('tenants:read')) {
      throw redirect({ to: '/settings/organization' });
    }

    // Territory admins (who passed the parent guard via territories:list) land on the Territories tab.
    // The Territories tab is their primary entry point since they reached /settings via their territory-level scope.
    if (hasPermission('territories:list')) {
      throw redirect({ to: '/settings/territories' });
    }

    // Location-scoped users (locations:list only) — fall through to SettingsIndexRedirect
    // which uses MasterContext and permission checks to redirect to the correct detail page.
    if (hasPermission('locations:list')) {
      return;
    }

    // No settings-eligible permission — redirect home as a safety net.
    throw redirect({ to: '/' });
  },
  component: SettingsIndexRedirect,
});

/**
 * Component-level redirect that reacts to MasterContext scope changes.
 * MasterContext and usePermissions are React contexts not available in `beforeLoad`,
 * so we use useEffect + useNavigate to redirect after mount.
 *
 * Permission-first logic:
 * 1. siteId + canAccessLocationSettings → navigate to /settings/locations/$locationId (checked first so
 *    cross-territory-locations users — who have a parent territoryId — land on their location).
 * 2. territoryId + canAccessTerritorySettings → navigate to /settings/territories/$territoryId (the same
 *    predicate the territory route's guard uses, so the redirect can never bounce back).
 *
 * This component is rendered only when `beforeLoad` did not throw a redirect
 * (i.e., location-scoped users with `locations:list` who need MasterContext to resolve their target).
 */
function SettingsIndexRedirect(): React.JSX.Element | null {
  const { siteId, territoryId } = useMasterContext();
  const permissions = usePermissions();
  const navigate = useNavigate();

  // Same predicates as the target routes' guards and the settings layout, so the three cannot disagree
  // and bounce back to /settings in a loop.
  const canOpenLocation = canAccessLocationSettings(permissions);
  const canOpenTerritory = canAccessTerritorySettings(permissions);

  useEffect(() => {
    // Location-scoped users: always go to location detail (permission-safe).
    // Checked first so cross-territory-locations users (who have territoryId set but lack territories:list)
    // are not incorrectly redirected to a territory detail page.
    if (siteId && canOpenLocation) {
      void navigate({ params: { locationId: siteId }, to: '/settings/locations/$locationId' });
      return;
    }
    // Territory-scoped users: go to territory detail when the territory route's guard would admit them.
    if (territoryId && canOpenTerritory) {
      void navigate({ params: { territoryId }, to: '/settings/territories/$territoryId' });
    }
  }, [siteId, territoryId, canOpenLocation, canOpenTerritory, navigate]);

  return null;
}
